import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { User, Check, AlertCircle, ChevronRight } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
type Role = 'learner' | 'parent' | 'teacher';
interface RegisterNameStepProps {
  role: Role;
  name: string;
  onNameChange: (value: string) => void;
  usernameAvailable: boolean;
  suggestedUsername: string;
  /** Alternatieven wanneer de kale voornaam bezet is. */
  suggestions: string[];
  /** Kind kiest een alternatief. */
  onPickSuggestion: (username: string) => void;
  /** Gekozen alternatief, of '' als het kind de voornaam gebruikt. */
  chosenUsername: string;
  slideVariants: Record<string, unknown>;
}
export function RegisterNameStep({
  role,
  name,
  onNameChange,
  usernameAvailable,
  suggestedUsername,
  suggestions,
  onPickSuggestion,
  chosenUsername,
  slideVariants
}: RegisterNameStepProps) {
  // Zodra het kind een alternatief heeft gekozen, is er niets meer bezet en
  // hoeft het formulier niet langer te waarschuwen.
  const resolved = chosenUsername || (usernameAvailable ? suggestedUsername : '');
  const needsChoice = !resolved;
  return <motion.div key="step-name" custom={1} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
    duration: 0.3
  }} className="space-y-5" data-id="element-388" data-mp="1vpnlhtl17z10">
      <h2 className="text-xl font-black text-slate-900 text-center" data-id="element-389" data-mp="2ngtochs50atz">
        {role === 'learner' ? 'Hoe heet je?' : 'Wat is je naam?'}
      </h2>

      <div data-id="element-390" data-mp="2eo9m77cmzo4e">
        <label className="block text-sm font-bold text-slate-600 mb-1.5" data-id="element-391" data-mp="2eojc6uxolva3">
          {role === 'learner' ? 'Voornaam' : 'Volledige naam'}
        </label>
        <div className="relative" data-id="element-392" data-mp="2in8qqqughivb">
          <Input type="text" required autoFocus value={name} onChange={e => onNameChange(e.target.value)} icon={<User className="w-5 h-5" data-id="element-394" data-mp="3l1miya5fymj6" />} className="bg-slate-50 border-2 border-slate-200 rounded-2xl focus:bg-white focus:border-orange-400 text-lg font-medium py-4" placeholder={role === 'learner' ? 'bijv. Djayden' : 'bijv. Jan de Vries'} data-id="element-393" data-mp="36ee1irljhkyr" />
        </div>
      </div>

      {/* Username preview for learners */}
      <AnimatePresence data-id="element-395" data-mp="371lk108qlp6c">
        {role === 'learner' && name.trim() && <motion.div initial={{
        opacity: 0,
        y: -10,
        height: 0
      }} animate={{
        opacity: 1,
        y: 0,
        height: 'auto'
      }} exit={{
        opacity: 0,
        y: -10,
        height: 0
      }} transition={{
        duration: 0.3
      }} className="overflow-hidden" data-id="element-396" data-mp="323k0yfbb9puv">
            <div className={`p-4 rounded-2xl border mt-2 ${needsChoice ? 'bg-amber-50 border-amber-200' : 'bg-orange-50 border-orange-100'}`} data-id="element-397" data-mp="2ecwe8nwqkvo9">
              <div className="flex items-start gap-3" data-id="element-398" data-mp="298c541jc9k3p">
                <div className={`mt-0.5 ${needsChoice ? 'text-amber-500' : 'text-orange-500'}`} data-id="element-399" data-mp="29714gal6wsgt">
                  {needsChoice ? <AlertCircle className="w-5 h-5" data-id="element-400" data-mp="2j6rwm5ma2vdq" /> : <Check className="w-5 h-5" data-id="element-401" data-mp="1lizxv77knm41" />}
                </div>
                <div className="min-w-0" data-id="element-402" data-mp="1qhuec2i2gykz">
                  <p className={`text-sm font-medium ${needsChoice ? 'text-amber-900' : 'text-orange-800'}`} data-id="element-403" data-mp="1qiw1348g39ij">
                    {needsChoice ? `Er is al iemand die ${suggestedUsername} heet. Kies er een:` : 'Je gebruikersnaam wordt:'}
                  </p>

                  {!needsChoice && <>
                      <p className="text-lg font-black mt-0.5 text-orange-600 break-all" data-id="element-404" data-mp="17q1wn48g6zsk">
                        {resolved}
                      </p>
                      <p className="text-xs mt-1 text-orange-600/70" data-id="element-405" data-mp="34hfrccc5z7d0">
                        Hiermee log je straks in. Onthoud hem goed!
                      </p>
                    </>}
                </div>
              </div>

              {/* Kiezen kan een kind van negen wél; verzinnen niet. Daarom
                  voorstellen in plaats van "probeer iets anders" — en bewust
                  geen suggestie om een achternaam toe te voegen: dat maakt een
                  pseudonieme naam herleidbaar tot een persoon, en die staat
                  daarna op het inlogscherm van een gedeeld apparaat. */}
              {needsChoice && <div className="mt-3 flex flex-wrap gap-2" data-id="element-406" data-mp="11idntesnh9x">
                  {suggestions.map(suggestion => <button key={suggestion} type="button" onClick={() => onPickSuggestion(suggestion)} className="px-3 py-2 rounded-xl bg-white border-2 border-amber-300 text-amber-900 font-bold text-sm hover:border-orange-400 hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-400 transition-colors" data-id="element-407" data-mp="27t9gh7onfiqa">
                      {suggestion}
                    </button>)}
                </div>}
            </div>
          </motion.div>}
      </AnimatePresence>

      <Button type="submit" variant="primary" size="lg" className="w-full" disabled={!name.trim() || role === 'learner' && !usernameAvailable} data-id="element-408" data-mp="4id4l3njmi88">
        Verder <ChevronRight className="w-5 h-5 ml-1" data-id="element-409" data-mp="3qpl43pbs7uwc" />
      </Button>
    </motion.div>;
}